import { doGet, doPost, doPut, doPatch, doDelete, createUrlQueryParams } from 'src/api/client';
import { getClientTimezone } from 'src/utils/date';

export async function reqPutDeclarationLifecycle(declarationId, lifecycle) {
  return doPut(`declarations/${declarationId}/lifecycle`, lifecycle);
}

export async function reqGetDeclarations(params) {
  if (params.declaration_type && typeof params.declaration_type === 'string')
    try {
      localStorage.setItem('lastDeclarationType', params.declaration_type.split('_').slice(0, 2).join('_')); // delta_t_* become delta_t
    } catch (error) {
      console.warn('Failed to persist declaration type to localStorage:', error);
    }
  return doGet(`declarations`, { ...params, timezone: params.timezone || getClientTimezone() });
}

export async function reqGetDeclarationFilterOptions(params) {
  return doGet(`declarations/filter-options`, params);
}

export async function reqGetDeclarationsCount(params) {
  return doGet(`declarations/count`, params);
}

export async function reqDeleteFileFromDeclaration(declarationId, fileId) {
  return doPatch(`declarations/${declarationId}/documents/${fileId}/deletion-date`);
}

export async function reqGetTemplates(params) {
  return doGet(`declarations/templates`, params);
}

export async function reqCreateTemplate(params) {
  return doPost(`declarations/templates`, params);
}

export async function reqGetArticleTemplate(params) {
  return doGet(`declarations/templates`, { ...params, template_type: 'article' });
}

export async function reqSendDeclToSoget(declaration) {
  return doPost(`declarations/old/send/declaration`, declaration);
}

export async function reqUpdateIsUpToDateFlag(declarationId, isUpToDate) {
  return doPatch(`declarations/${declarationId}/is-up-to-date`, { is_up_to_date: isUpToDate });
}

export async function reqGetDeclarationDocuments(declarationId) {
  return doGet(`declarations/${declarationId}/documents`);
}

export async function reqGetTeledecResponses(idTeledec) {
  return doGet(`declarations/old/${idTeledec}/responses`);
}

export async function reqGetOldTeledec(decId, customsStatus) {
  return doGet(`declarations/old/${decId}/previous`, { customsStatus });
}

export async function reqPostSOneArticles(declaration_id, declaration_info) {
  return doPost(`declarations/sone-validation`, { declaration_id, declaration_info }, true);
}

export async function reqRemoveTagFromDeclaration(tagId, declarationId) {
  return doDelete(`declarations/${declarationId}/tags/${tagId}`);
}

export async function reqAddTagToDeclaration(declarationId, tagIds) {
  const normalizedTagIds = Array.isArray(tagIds) ? tagIds : [tagIds];
  return doPost(`declarations/${declarationId}/tags`, { tagIds: normalizedTagIds });
}

export async function reqGetDeclarationTags(teledeclarationId) {
  return doGet(`declarations/${teledeclarationId}/tags`);
}

export async function reqCheckTeledecFolderReference(folderReference) {
  return doGet(`declarations/old/check_folder_reference`, { folderReference });
}

export async function reqPatchUpdateNameDec(declarationId, name) {
  return doPatch(`declarations/${declarationId}/name`, { name });
}

export async function reqUpdateTeledeclaration(teledeclaration, forceUpdate) {
  return doPut(`declarations/old/${teledeclaration.id}/values`, { teledeclaration, force_update: forceUpdate });
}

export async function reqGetDeclarationsResponses(declarationType, companyId = null, startDate = null, endDate = null) {
  return doGet(`declarations/old/responses`, { declarationType, companyId, startDate, endDate });
}

export async function reqDeclarationSendAudit(audit) {
  return doPost(`declarations/old/send/audit`, audit);
}

export async function reqSetTeledeclarationAudited(declarationId) {
  return doPut(`declarations/old/${declarationId}/audit`);
}

export async function reqSetProcessStatus(declarationId, processStatus) {
  return doPut(`declarations/old/${declarationId}/process_status`, processStatus);
}

export async function reqSetTeledeclarationAuditable(teledecIds, isDischargeable) {
  return doPut(`declarations/old/auditable`, { teledec_ids: teledecIds, is_dischargeable: isDischargeable });
}

export async function reqGetTeledecById(teledecId) {
  return doGet(`declarations/old/${teledecId}`);
}

export async function reqUpdateDeclarationTemplate(templateId, template_type, data) {
  return doPut(`declarations/templates/${templateId}`, { template_type, data });
}

export async function reqAddDeclarationTemplate(template, template_type, direction = null, declaration_type = null) {
  return doPost(`declarations/templates`, { data: template, template_type, direction, declaration_type });
}

export async function reqDeleteDeclarationTemplate(idTemplate) {
  return doPatch(`declarations/templates/${idTemplate}/deletion-date`);
}

export async function reqGetDeclarationsStatistics(params, lang, as_excel) {
  return doGet(
    `declarations/statistics`,
    { ...params, timezone: params.timezone || getClientTimezone(), lang, as_excel },
    { responseType: 'blob' }
  );
}

/**
 * Uploads a periodic customs statement PDF and downloads the enriched Excel file.
 * @param file - The periodic customs statement PDF file to upload.
 * @returns A promise that resolves when the file is uploaded and downloaded.
 */
export async function reqConvertPeriodicCustomsStatementToExcel(file) {
  const formData = new FormData();
  formData.append('file', file);
  return doPost('declarations/periodic-customs-statements/to-excel', formData, false);
}

export async function reqGetStatisticsColumns(lang, declarationType) {
  return doGet(`declarations/statistics/columns`, { lang, declarationType });
}

export async function reqSaveStatisticsModel(params) {
  return doPost(`declarations/statistics/model`, params);
}

export async function reqSaveGlobalPredefinedStatisticsModel(params) {
  return doPost(`declarations/statistics/model/predefined`, params);
}

export async function reqGetStatisticsModels(companyId = null) {
  return doGet(`declarations/statistics/models`, companyId ? { companyId } : undefined);
}

export async function reqGetProcessingTime() {
  return doGet(`declarations/processing-time`);
}

export async function reqGetProcessingTimeSummary() {
  return doGet(`declarations/processing-time/summary`);
}

export async function reqGetPendingResponses(companyId, startDate, endDate, declarationTypes) {
  return doGet(`declarations/pending-responses`, {
    companyId,
    startDate,
    endDate,
    declarationTypes: declarationTypes
  });
}

export async function reqDeleteStatisticsModel(modelId) {
  return doDelete(`declarations/statistics/model/${modelId}`);
}

export async function reqDeletePredefinedStatisticsModel(modelId) {
  return doDelete(`declarations/statistics/model/predefined/${modelId}`);
}

export async function reqGetStatisticsModelEmailSettings(statisticsModelId) {
  return doGet(`declarations/statistics/model/${statisticsModelId}/email`);
}

export async function reqCreateStatisticsModelEmailSettings(statisticsModelId, params) {
  return doPost(`declarations/statistics/model/${statisticsModelId}/email`, params);
}

export async function reqUpdateStatisticsModelEmailSettings(statisticsModelId, params) {
  return doPut(`declarations/statistics/model/${statisticsModelId}/email`, params);
}

export async function reqGetDeclaration(declarationId) {
  return doGet(`declarations/${declarationId}`);
}

export async function reqGetCustomsFlows(declarationId) {
  return doGet(`declarations/${declarationId}/customs-flows`);
}

export async function reqUpdateDeclaration(declarationId, declarationType, declaration) {
  // Same payload as the creation, and as what reading a declaration returns. What is sent replaces what
  // is stored, so a key left out is a key removed: send the declaration whole, not just what changed.
  return doPut(`declarations/${declarationId}?${createUrlQueryParams({ declarationType })}`, declaration);
}

export async function reqUpdateDeclarationExtraData(declarationId, extraData) {
  return doPut(`declarations/${declarationId}/extra-data`, { extraData });
}

export async function reqUpdateDeclarationToTemplate(declarationId, duplicate = false) {
  return doPut(`declarations/${declarationId}/template`, { duplicate });
}

export async function reqAddDocumentsToDeclaration(declarationId, documents) {
  const dataParameters = new FormData();
  documents.forEach((document) => {
    dataParameters.append('documents', document);
  });
  return doPost(`declarations/${declarationId}/documents`, dataParameters, false);
}

export async function reqGetTeledecDocuments(declarationId) {
  return doGet(`declarations/${declarationId}/documents`);
}

export async function reqGetDecDocumentGeneration(declarationId, type, subType, extraData, timestamp) {
  return doPost(
    `declarations/${declarationId}/documents/generate?${createUrlQueryParams({ type, sub_type: subType, timestamp })}`,
    { extra_data: extraData }
  );
}

export async function reqDeleteFileTeledec(declarationId, documentId) {
  return doPatch(`declarations/${declarationId}/documents/${documentId}/deletion-date`);
}

export async function reqCreateDeclaration(declarationType, declaration) {
  return doPost(`declarations?${createUrlQueryParams({ declarationType })}`, declaration);
}

export async function reqDuplicateDeclaration(declarationId, params) {
  return doPost(`declarations/${declarationId}/duplicate`, params);
}

export async function reqGetDeclarationLifecycle(declarationId) {
  return doGet(`declarations/${declarationId}/lifecycle`);
}

export async function reqSendDeclarationToCustoms(
  declarationId,
  declaration,
  action,
  declarationType,
  schedule: string | null = null
) {
  return doPost(
    `declarations/${declarationId}/send/customs?${createUrlQueryParams({ action, declarationType, schedule })}`,
    declaration
  );
}

export async function reqCancelScheduledMessage(declarationId: string) {
  return doPost(`declarations/message/${declarationId}/schedule/cancel`);
}

export async function reqSendScheduledMessageNow(declarationId: string) {
  return doPost(`declarations/message/${declarationId}/schedule/send`);
}

export async function reqGetDeltaTNotificationMrnAvailability(mrn, declarationId) {
  return doGet('declarations/delta-t/notifications/mrn-availability', {
    mrn,
    declaration_id: declarationId
  });
}

export async function reqSendCCSMessage(declarationId, ccsAction, ccsTracingEvent) {
  return doPost(`declarations/${declarationId}/send/ccs?${createUrlQueryParams({ ccsAction, ccsTracingEvent })}`);
}

export async function reqSendICS2Message(declarationId, params) {
  return doPost(`declarations/${declarationId}/send/ics2?${createUrlQueryParams(params)}`);
}

export async function reqGetDeclarationHistoryDiff(declarationId, modificationDate) {
  return doGet(`declarations/${declarationId}/history/diff`, { modificationDate });
}

export async function reqDeleteDeclaration(declarationId) {
  return doPatch(`declarations/${declarationId}/deletion-date`);
}

export async function reqRestoreDeclarationToLastCustomsStatus(declarationId) {
  return doPut(`declarations/${declarationId}/lifecycle/customs_status/restore`);
}
